import { useState, useEffect, useRef } from 'react'
import BottomNav from '../components/BottomNav'
import { useAuth } from '../contexts/AuthContext'
import { config } from '../config'
import { getUserBalance, getBetRecords } from '../services/api'
import type {BetRecordDTO} from "../types/betting.ts"

export default function Profile() {
  const { user, logout } = useAuth()

  // 用戶狀態
  const [balance, setBalance] = useState<number | null>(null)
  const [isLoadingBalance, setIsLoadingBalance] = useState(true)
  const username = user?.username || '幸運玩家'
  const vipLevel = 'VIP 3'

  // 充值/提現模態框
  const [showRecharge, setShowRecharge] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const [rechargeAmount, setRechargeAmount] = useState('')
  const [withdrawAmount, setWithdrawAmount] = useState('')

  // 投注記錄 - 使用 ref 来跟踪实时页码
  const [betHistory, setBetHistory] = useState<BetRecordDTO[]>([])
  const [isLoadingBetRecords, setIsLoadingBetRecords] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(true)

  // 🔧 使用 ref 来跟踪实时页码，避免异步状态问题
  const currentPageRef = useRef(1)

  // refs
  const betRecordsContainerRef = useRef<HTMLDivElement>(null)

  // 🔧 简化：获取投注记录
  const loadBetRecords = async (page: number = 1, append: boolean = false) => {
    console.log(`📥 加载第${page}页`)

    if (append) {
      setIsLoadingMore(true)
    } else {
      setIsLoadingBetRecords(true)
      setBetHistory([]) // 清空现有数据
      currentPageRef.current = 1 // 🔧 重置页码
    }

    try {
      const result = await getBetRecords({
        pageIndex: page,
        pageSize: 3
      })

      console.log(`✅ 第${page}页返回${result.data.length}条数据`)

      if (append) {
        // 检查是否有新数据
        const existingIds = new Set(betHistory.map(bet => bet.betId))
        const newData = result.data.filter(bet => !existingIds.has(bet.betId))

        if (newData.length > 0) {
          setBetHistory(prev => [...prev, ...newData])
          currentPageRef.current = page // 🔧 更新 ref 页码
          console.log(`✅ 添加了${newData.length}条新数据，当前页码：${page}`)
        } else {
          setCanLoadMore(false)
          console.log(`🛑 没有新数据，停止加载`)
        }
      } else {
        setBetHistory(result.data)
        currentPageRef.current = page // 🔧 更新 ref 页码
        setCanLoadMore(result.data.length >= 3) // 如果返回少于3条，说明没有更多了
      }

    } catch (error) {
      console.error('❌ 加载失败:', error)
      if (!append) {
        setBetHistory([])
      }
    } finally {
      setIsLoadingBetRecords(false)
      setIsLoadingMore(false)
    }
  }

  // 🔧 简化：滚动加载更多
  const handleScroll = () => {
    const container = betRecordsContainerRef.current
    if (!container || isLoadingMore || !canLoadMore) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const scrollBottom = scrollHeight - scrollTop - clientHeight

    if (scrollBottom < 50) {
      const nextPage = currentPageRef.current + 1 // 🔧 使用 ref 获取实时页码
      console.log(`🚀 触发加载第${nextPage}页（当前页码：${currentPageRef.current}）`)
      loadBetRecords(nextPage, true)
    }
  }

  // 添加滚动监听
  useEffect(() => {
    const container = betRecordsContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true })
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [isLoadingMore, canLoadMore, betHistory]) // 🔧 移除 currentPage 依赖

  // 🔧 获取用户余额
  const loadUserBalance = async () => {
    try {
      const userBalance = await getUserBalance()
      setBalance(userBalance)
    } catch (error) {
      console.error('获取用户余额失败:', error)
      setBalance(null)
    } finally {
      setIsLoadingBalance(false)
    }
  }

  // 组件加载时获取数据
  useEffect(() => {
    loadUserBalance()
    loadBetRecords(1, false)
  }, [])

  // 刷新功能
  const refreshBetRecords = () => {
    currentPageRef.current = 1 // 🔧 重置 ref 页码
    setCanLoadMore(true)
    loadBetRecords(1, false)
  }

  const refreshBalance = () => {
    loadUserBalance()
  }

  // 充值處理
  const handleRecharge = async () => {
    if (!rechargeAmount || parseFloat(rechargeAmount) <= 0) {
      showToast('請輸入有效的充值金額')
      return
    }

    if (balance === null) {
      showToast('余额信息加载中，请稍后再试')
      return
    }

    setBalance((prev: number | null) => prev ? prev + parseFloat(rechargeAmount) : parseFloat(rechargeAmount))
    setRechargeAmount('')
    setShowRecharge(false)
    showToast(`充值成功！金額：${rechargeAmount}元`)

    setTimeout(() => {
      refreshBalance()
      refreshBetRecords()
    }, 1000)
  }

  // 提現處理
  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount)
    if (!withdrawAmount || amount <= 0) {
      showToast('請輸入有效的提現金額')
      return
    }

    if (balance === null) {
      showToast('余额信息加载中，请稍后再试')
      return
    }

    if (amount > balance) {
      showToast('提現金額不能超過餘額')
      return
    }

    setBalance((prev: number | null) => prev ? prev - amount : 0)
    setWithdrawAmount('')
    setShowWithdraw(false)
    showToast(`提現申請已提交！金額：${withdrawAmount}元`)

    setTimeout(() => {
      refreshBalance()
      refreshBetRecords()
    }, 1000)
  }

  // 格式化时间戳为可读格式
  const formatTime = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 获取投注状态显示文本
  const getBetStatusText = (winStatus: number, betStatus: number): string => {
    if (betStatus === 2) return '无效'
    if (winStatus === 0) return '未开奖'
    if (winStatus === 1) return '中奖'
    if (winStatus === 2) return '未中奖'
    return '未知'
  }

  // 获取投注状态样式类名
  const getBetStatusClass = (winStatus: number, betStatus: number): string => {
    if (betStatus === 2) return 'bet-status-invalid'
    if (winStatus === 0) return 'bet-status-pending'
    if (winStatus === 1) return 'bet-status-win'
    if (winStatus === 2) return 'bet-status-lose'
    return 'bet-status-unknown'
  }

  // Toast 提示
  const showToast = (message: string) => {
    const toast = document.createElement('div')
    toast.textContent = message
    toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 184, 148, 0.95);
      color: white;
      padding: 12px 24px;
      border-radius: 25px;
      z-index: 10000;
      font-weight: bold;
      box-shadow: 0 4px 15px rgba(0, 184, 148, 0.3);
    `
    document.body.appendChild(toast)
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast)
      }
    }, config.ui.toastDuration)
  }

  return (
      <div style={{
        fontFamily: 'Microsoft YaHei, sans-serif',
        background: 'var(--gradient-purple)',
        minHeight: '100vh',
        padding: '20px',
        paddingBottom: '100px',
        margin: 0
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          background: 'var(--bg-card)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-heavy)',
          overflow: 'hidden'
        }}>
          {/* 個人檔案頭部 */}
          <div className="profile-header-enhanced">
            <div className="user-avatar-enhanced">
              👤
            </div>
            <h2 style={{ margin: '10px 0', fontSize: '2em' }}>{username}</h2>
            <div className="vip-badge">{vipLevel}</div>
            <p style={{ margin: '10px 0', opacity: 0.9, fontSize: '1.1em' }}>
              歡迎回來，祝您好運連連！
            </p>
          </div>

          {/* 餘額展示 */}
          <div className="balance-showcase">
            <div className="balance-main">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.3em' }}>💰 我的餘額</h3>
                <button
                    onClick={refreshBalance}
                    disabled={isLoadingBalance}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '15px',
                      fontSize: '0.8em',
                      cursor: isLoadingBalance ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: isLoadingBalance ? 0.6 : 1
                    }}
                >
                  {isLoadingBalance ? '刷新中...' : '🔄 刷新'}
                </button>
              </div>

              <div className="balance-amount-large" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}>
                {isLoadingBalance ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      加载中...
                    </>
                ) : balance !== null ? (
                    `¥${balance.toFixed(2)}`
                ) : (
                    <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8em' }}>
                  获取余额失败
                </span>
                )}
              </div>
              <p style={{ margin: '10px 0', opacity: 0.8 }}>賬戶安全，資金保障</p>

              <div className="balance-actions">
                <button
                    className="balance-btn-enhanced"
                    onClick={() => setShowRecharge(true)}
                    disabled={balance === null}
                    style={{ opacity: balance === null ? 0.5 : 1 }}
                >
                  💳 充值
                </button>
                <button
                    className="balance-btn-enhanced"
                    onClick={() => setShowWithdraw(true)}
                    disabled={balance === null}
                    style={{ opacity: balance === null ? 0.5 : 1 }}
                >
                  💸 提現
                </button>
              </div>

              {/* 登出按鈕 */}
              <div style={{ marginTop: '15px', textAlign: 'center' }}>
                <button
                    onClick={logout}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      padding: '10px 20px',
                      borderRadius: '20px',
                      fontSize: '0.9em',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                >
                  🚪 登出
                </button>
              </div>
            </div>
          </div>

          {/* 投注記錄 */}
          <div style={{ padding: '20px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.4em' }}>
                📊 投注記錄
              </h3>
              <button
                  onClick={refreshBetRecords}
                  disabled={isLoadingBetRecords}
                  style={{
                    background: 'rgba(116, 75, 162, 0.1)',
                    border: '2px solid rgba(116, 75, 162, 0.3)',
                    color: 'var(--text-primary)',
                    padding: '5px 10px',
                    borderRadius: '15px',
                    fontSize: '0.8em',
                    cursor: isLoadingBetRecords ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: isLoadingBetRecords ? 0.6 : 1
                  }}
              >
                {isLoadingBetRecords ? '加载中...' : '🔄 刷新'}
              </button>
            </div>

            {/* 投注记录容器 */}
            <div
                ref={betRecordsContainerRef}
                style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '10px',
                  padding: '10px'
                }}
            >
              {isLoadingBetRecords && betHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      border: '3px solid rgba(116, 75, 162, 0.3)',
                      borderTop: '3px solid var(--primary-red)',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 15px'
                    }} />
                    正在加载投注记录...
                  </div>
              ) : betHistory.length > 0 ? (
                  <>
                    {betHistory.map((bet, index) => (
                        <div key={`${bet.betId}-${index}`} className="bet-record-card">
                          <div className="bet-record-header">
                      <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                        {bet.gameName || bet.gameCode}
                      </span>
                            <span className={getBetStatusClass(bet.winStatus, bet.betStatus)}>
                        {getBetStatusText(bet.winStatus, bet.betStatus)}
                      </span>
                          </div>
                          <div style={{ margin: '10px 0' }}>
                            <strong>{bet.betTypeName || bet.betType}</strong> |
                            投注：{bet.betAmount}元 |
                            赔率：{bet.odds}倍
                          </div>
                          <div style={{ margin: '5px 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                            期号：{bet.periodNo}
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.9em',
                            color: 'var(--text-secondary)'
                          }}>
                            <span>{formatTime(bet.betTime)}</span>
                            <span style={{
                              color: bet.winStatus === 1 ? 'var(--success-green)' :
                                  bet.winStatus === 2 ? 'var(--primary-red)' : 'var(--text-secondary)',
                              fontWeight: 'bold'
                            }}>
                        {bet.winStatus === 1 ? `+${bet.actualWinAmount || bet.possibleWinAmount}元` :
                            bet.winStatus === 2 ? `-${bet.betAmount}元` :
                                `可赢${bet.possibleWinAmount}元`}
                      </span>
                          </div>
                        </div>
                    ))}

                    {isLoadingMore && (
                        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid rgba(116, 75, 162, 0.3)',
                            borderTop: '2px solid var(--primary-red)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 10px'
                          }} />
                          正在加载更多...
                        </div>
                    )}

                    {!canLoadMore && !isLoadingMore && (
                        <div style={{
                          textAlign: 'center',
                          padding: '20px',
                          color: 'var(--text-secondary)',
                          fontSize: '0.9em',
                          opacity: 0.7
                        }}>
                          — 已显示全部记录 —
                        </div>
                    )}
                  </>
              ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--text-secondary)'
                  }}>
                    <div style={{ fontSize: '3em', marginBottom: '15px' }}>📝</div>
                    <p>暂无投注记录</p>
                    <p style={{ fontSize: '0.9em', opacity: 0.7 }}>快去下注赢取奖金吧！</p>
                  </div>
              )}
            </div>
          </div>
        </div>

        {/* 充值模態框 */}
        {showRecharge && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--bg-overlay)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}>
              <div className="luxury-card" style={{ width: '90%', maxWidth: '400px', margin: 0 }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px', color: 'var(--text-primary)' }}>
                  💳 充值
                </h3>

                <div style={{
                  marginBottom: '15px',
                  textAlign: 'center',
                  color: 'var(--text-secondary)',
                  fontSize: '0.9em'
                }}>
                  當前餘額：{balance !== null ? `¥${balance.toFixed(2)}` : '获取中...'}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    充值金額
                  </label>
                  <input
                      type="number"
                      value={rechargeAmount}
                      onChange={(e) => setRechargeAmount(e.target.value)}
                      placeholder="請輸入充值金額"
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #e9ecef',
                        borderRadius: '10px',
                        fontSize: '1.1em',
                        textAlign: 'center'
                      }}
                  />
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px',
                  marginBottom: '20px'
                }}>
                  {[100, 500, 1000, 2000, 5000, 10000].map(amount => (
                      <button
                          key={amount}
                          onClick={() => setRechargeAmount(amount.toString())}
                          style={{
                            padding: '10px',
                            border: '2px solid var(--primary-gold)',
                            borderRadius: '10px',
                            background: 'white',
                            color: 'var(--text-primary)',
                            fontWeight: 'bold',
                            cursor: 'pointer'
                          }}
                      >
                        {amount}元
                      </button>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <button
                      onClick={() => setShowRecharge(false)}
                      style={{
                        padding: '15px',
                        border: '2px solid var(--text-secondary)',
                        borderRadius: '10px',
                        background: 'white',
                        color: 'var(--text-secondary)',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                  >
                    取消
                  </button>
                  <button
                      onClick={handleRecharge}
                      className="confirm-bet-btn-enhanced"
                      style={{ padding: '15px', fontSize: '1em' }}
                  >
                    確認充值
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* 提現模態框 */}
        {showWithdraw && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--bg-overlay)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}>
              <div className="luxury-card" style={{ width: '90%', maxWidth: '400px', margin: 0 }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px', color: 'var(--text-primary)' }}>
                  💸 提現
                </h3>

                <div style={{ marginBottom: '15px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  可用餘額：{balance !== null ? `¥${balance.toFixed(2)}` : '获取中...'}
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    提現金額
                  </label>
                  <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="請輸入提現金額"
                      max={balance || 0}
                      style={{
                        width: '100%',
                        padding: '15px',
                        border: '2px solid #e9ecef',
                        borderRadius: '10px',
                        fontSize: '1.1em',
                        textAlign: 'center'
                      }}
                  />
                </div>

                <button
                    onClick={() => balance !== null && setWithdrawAmount(balance.toString())}
                    disabled={balance === null}
                    style={{
                      width: '100%',
                      padding: '10px',
                      marginBottom: '20px',
                      border: '2px solid var(--success-green)',
                      borderRadius: '10px',
                      background: balance !== null ? 'rgba(0, 184, 148, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                      color: balance !== null ? 'var(--success-green)' : '#999',
                      fontWeight: 'bold',
                      cursor: balance !== null ? 'pointer' : 'not-allowed',
                      opacity: balance !== null ? 1 : 0.5
                    }}
                >
                  全部提現
                </button>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <button
                      onClick={() => setShowWithdraw(false)}
                      style={{
                        padding: '15px',
                        border: '2px solid var(--text-secondary)',
                        borderRadius: '10px',
                        background: 'white',
                        color: 'var(--text-secondary)',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                  >
                    取消
                  </button>
                  <button
                      onClick={handleWithdraw}
                      className="confirm-bet-btn-enhanced"
                      style={{ padding: '15px', fontSize: '1em' }}
                  >
                    確認提現
                  </button>
                </div>
              </div>
            </div>
        )}

        {/* 底部導航 */}
        <BottomNav />

        <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .bet-status-win {
          background: var(--success-green);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: bold;
        }
        
        .bet-status-lose {
          background: var(--primary-red);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: bold;
        }
        
        .bet-status-pending {
          background: var(--primary-gold);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: bold;
        }
        
        .bet-status-invalid {
          background: #999;
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: bold;
        }
        
        .bet-status-unknown {
          background: var(--text-secondary);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8em;
          font-weight: bold;
        }
      `}</style>
      </div>
  )
}